import { TableTagCell } from "@src/components/data-components/DocumentationCollection/cells/CustomFieldTagCell";
import { TagsSelectPopup } from "@src/components/deprecated/TagsSelectPopup/TagsSelectPopup";
import { useTaskTypeById } from "@src/data-hooks/task-types/useTaskTypes";
import { translate } from "@src/libs/i18next/translations.util";
import { ExportDeviationPDFsPopup } from "@src/pages/Deviations/Content/Overview/Other/ExportDeviationPDFPopup";
import { DeviationsLoader } from "@src/to-be-removed/api/deviations/deviations.loader";
import { Tag } from "data-hooks/company/tags/tags.type";
import { t } from "i18next";
import { ActionValidator } from "../../../../../auth/actionValidator";
import { DataTableData, DataTableHeader, DataTableV2Element } from "../../../../../components/deprecated/DataTable/DataTable";
import { DeviationTypeSelectorPopup } from "../../../../../components/deprecated/DeviationTypeSelectorPopup/DeviationTypeSelectorPopup";
import { Label } from "../../../../../components/deprecated/Label/Label";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { StatusTag } from "../../../../../components/deprecated/StatusTag/StatusTag";
import { UserNameLabel } from "../../../../../components/deprecated/Text/Label/UserNameLabel";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { DeviationData } from "../../../Deviation/DeviationData";
import { DeviationPriority } from "../../../Deviation/DeviationPriority";
import { DeviationStatus } from "../../../Deviation/DeviationStatus";
import { PropertyData } from "../../../Property/PropertyData";
import {
  DataObjectRawTagField,
  DeviationDataInvoicedToField,
  DeviationDataPriorityField,
  DeviationDataStatusField,
  DeviationDataTaskTypeIDField,
  DeviationDataVisibilityField,
} from "../../DataObjectField/CustomFields/DeviationDataFields";
import { FirebaseDataContractorField } from "../../DataObjectField/CustomFields/FirebaseDataContractorField";
import { FirebaseDataApiClientField } from "../../DataObjectField/CustomFields/FirebaseDataFields";
import { FirebaseDataFirebaseFileField } from "../../DataObjectField/CustomFields/FirebaseDataFirebaseFileField";
import { FirebaseDataPropertyField } from "../../DataObjectField/CustomFields/FirebaseDataPropertyField";
import { FirebaseDataSourceField } from "../../DataObjectField/CustomFields/FirebaseDataSourceField";
import { FirebaseDataUnitField } from "../../DataObjectField/CustomFields/FirebaseDataUnitField";
import { FirebaseDataUserField } from "../../DataObjectField/CustomFields/FirebaseDataUserField";
import { PropertyDataFloorsField } from "../../DataObjectField/CustomFields/PropertyFields";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { FirebaseDataBooleanField } from "../../DataObjectField/Generic/FirebaseDataBooleanField";
import { FirebaseDataDateField } from "../../DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataNumberField } from "../../DataObjectField/Generic/FirebaseDataNumberField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { DeviationDataActions } from "./DeviationDataActions";
import { DeviationStatisticsHelper } from "./DeviationStatisticsHelper";
import { getTotalTaskCosts, getTotalTaskHours, loadTotalTaskCosts, loadTotalTaskHours } from "./statistics/deviations.costs.utils";

export class DeviationDataHelper extends DataObjectHelper<DeviationData> {
  id = () => "deviation";

  displayName = () => t("main.task");
  displayNamePlural = () => t("main.tasks");

  createdByApiClient = new FirebaseDataApiClientField<DeviationData>(
    "createdBy.apiClient",
    t("main.createdByApiClient"),
    (data) => data.createdBy?.apiClient
  );
  createdBySource = new FirebaseDataSourceField<DeviationData>("createdBy.source", t("main.source"), (data) => data.createdBy?.source, "source");
  type = new DeviationDataTaskTypeIDField<DeviationData>(
    "type",
    t("main.type"),
    (task) => task.taskTypeID ?? task.customTaskType?.id ?? task.taskType ?? "deviation",
    "build"
  );
  property = new FirebaseDataPropertyField<DeviationData>(
    "property",
    t("main.property"),
    (deviation) => deviation.parent as PropertyData | undefined,
    "business",
    false
  );
  units = new FirebaseDataUnitField<DeviationData>("unitIDs", t("main.tenants"), (deviation) => deviation.units, "meeting_room", true);
  status = new DeviationDataStatusField<DeviationData>(
    "status",
    t("main.status"),
    (deviation) => (deviation.status instanceof DeviationStatus ? deviation.status : DeviationStatus.getStatusWithId(deviation.status)),
    "done"
  );
  userID = new FirebaseDataUserField<DeviationData>("userID", t("main.reportedBy"), (deviation) => deviation.user, "person_add");
  title = new FirebaseDataStringField<DeviationData>("title", t("main.title"), (deviation) => deviation.title, "title");
  description = new FirebaseDataStringField<DeviationData>("description", t("main.description"), (deviation) => deviation.description, "edit");
  deviationNumber = new FirebaseDataStringField<DeviationData>(
    "deviationNumber",
    t("main.deviationNumber"),
    (deviation) => deviation.deviationNumber,
    "tag"
  );

  priority = new DeviationDataPriorityField<DeviationData>(
    "priority",
    t("main.priority"),
    (deviation) => (deviation.priority instanceof DeviationPriority ? deviation.priority : DeviationPriority.getPriorityWithId(deviation.priority)),
    "priority_high"
  );
  tags = new DataObjectRawTagField<DeviationData>("tags", t("main.tags"), (deviation) => deviation.tags, "category", true);
  reportDate = new FirebaseDataDateField<DeviationData>("reportDate", t("main.reportDate"), (deviation) => deviation.reportDate, "schedule", false);
  lastUpdate = new FirebaseDataDateField<DeviationData>("lastUpdate", t("main.lastUpdated"), (deviation) => deviation.lastUpdate, "update");
  floors = new PropertyDataFloorsField<DeviationData>("floors", t("main.floors"), (deviation) => deviation.floors, "trending_up", true);
  placeDescription = new FirebaseDataStringField<DeviationData>(
    "placeDescription",
    t("main.placeDescription"),
    (deviation) => deviation.placeDescription,
    "place"
  );
  startDate = new FirebaseDataDateField<DeviationData>("startDate", t("main.startDate"), (deviation) => deviation.startDate, "play_arrow");
  endDate = new FirebaseDataDateField<DeviationData>("endDate", t("main.endDate"), (deviation) => deviation.endDate, "last_page");
  contractorGroups = new FirebaseDataContractorField<DeviationData>(
    "contractorGroups",
    t("main.contractorGroups"),
    (deviation) => deviation.contractorGroups,
    "build_circle",
    true
  );
  responsibles = new FirebaseDataUserField<DeviationData>(
    "responsibles",
    t("main.responsibles"),
    (deviation) => deviation.responsibles,
    "supervisor_account",
    true
  );
  executors = new FirebaseDataUserField<DeviationData>("executors", t("main.executors"), (deviation) => deviation.executors, "people_alt", true);
  archived = new FirebaseDataBooleanField<DeviationData>(
    "archived",
    t("main.isArchived"),
    (deviation) => deviation.archived || (deviation.parent as PropertyData)?.archived,
    "archive"
  );
  files = new FirebaseDataFirebaseFileField<DeviationData>("files", t("main.files"), (deviation) => deviation.files, "description", true);
  images = new FirebaseDataFirebaseFileField<DeviationData>("images", t("main.images"), (deviation) => deviation.images, "photo_camera", true);
  seenBy = new FirebaseDataUserField<DeviationData>(
    "seenBy",
    t("main.seenBy"),
    (deviation) => deviation.seenBy?.map((seenBy) => seenBy.user),
    "visibility",
    true
  );
  visibility = new DeviationDataVisibilityField<DeviationData>(
    "visibility",
    t("main.visibility"),
    (deviation) => deviation.visibility,
    "visibility_off"
  );
  invoicedTo = new DeviationDataInvoicedToField<DeviationData>(
    "invoicedTo",
    t("main.invoicedTo"),
    (deviation) => deviation.invoicedTo,
    "attach_money"
  );
  responseTime = new FirebaseDataNumberField<DeviationData>(
    "responseTime",
    t("main.responseTime"),
    (data) => DeviationStatisticsHelper.getDeviationStatistics(data).getResponseTime(),
    "schedule",
    false,
    { metric: "dynamicDate", rawDataMetric: "minutes" }
  );
  repairTime = new FirebaseDataNumberField<DeviationData>(
    "repairTime",
    t("main.repairTime"),
    (data) => DeviationStatisticsHelper.getDeviationStatistics(data).getRepairTime(),
    "schedule",
    false,
    { metric: "dynamicDate", rawDataMetric: "minutes" }
  );

  override allFields() {
    return [
      this.type,
      this.property,
      this.units,
      this.status,
      this.userID,
      this.title,
      this.description,
      this.deviationNumber,
      this.priority,
      this.tags,
      this.reportDate,
      this.lastUpdate,
      this.floors,
      this.placeDescription,
      this.startDate,
      this.endDate,
      this.contractorGroups,
      this.responsibles,
      this.executors,
      this.archived,
      this.files,
      this.images,
      this.seenBy,
      this.visibility,
      this.invoicedTo,
      this.createdByApiClient,
      this.responseTime,
      this.repairTime,
      this.createdBySource,
    ];
  }

  override getAllDatas(): DeviationData[] {
    return DeviationsLoader.getInstance().getAllDeviationDatas();
  }

  override loadAll(): Promise<DeviationData[]> {
    return DeviationsLoader.getInstance()
      .loadAllDeviations()
      .then(() => DeviationsLoader.getInstance().getAllDeviationDatas());
  }

  getUniqueIDForData(data: DeviationData): string {
    const parentID = data.parent?.id;
    return parentID ? `${parentID}-${data.id}` : data.id;
  }

  override getAllDataTableDatas(documents: DeviationData[], fields?: string[]): DataTableData[] {
    return documents.map((deviation) => {
      const parentID = deviation.parent?.id;
      const dataTableData: DataTableData = {
        key: parentID ? parentID + "-" + deviation.id : deviation.id,
        cells: {},
      };

      if (!fields || fields.includes("type")) {
        dataTableData.cells["type"] = {
          element: <DeviationTypeCell taskTypeID={deviation.taskTypeID} />,
          rawString: this.type.getStringRepresentation(deviation.taskTypeID),
          rawData: deviation.taskTypeID,
        };
      }
      if (!fields || fields.includes("property")) {
        dataTableData.cells["property"] = deviation.parent instanceof PropertyData ? deviation.parent.name : "";
      }
      if (!fields || fields.includes("units")) {
        dataTableData.cells["units"] = deviation.units.map((unit) => unit.name).join(", ");
      }
      if (!fields || fields.includes("status")) {
        dataTableData.cells["status"] = {
          element: (
            <StatusTag
              text={deviation.status.name}
              color={
                deviation.status === DeviationStatus.NOT_STARTED ? "red" : deviation.status === DeviationStatus.BEING_SOLVED ? "orange" : "green"
              }
            />
          ),
          rawString: deviation.status.name,
        };
      }
      if (!fields || fields.includes("userID")) {
        dataTableData.cells["userID"] = {
          rawString: deviation.user?.name,
          element: <UserNameLabel user={deviation.user} type="paragraph" />,
        };
      }
      if (!fields || fields.includes("title")) {
        dataTableData.cells["title"] = deviation.title;
      }
      if (!fields || fields.includes("description")) {
        dataTableData.cells["description"] = deviation.description;
      }
      if (!fields || fields.includes("deviationNumber")) {
        dataTableData.cells["deviationNumber"] = deviation.deviationNumber;
      }
      if (!fields || fields.includes("priority")) {
        dataTableData.cells["priority"] = {
          element: (
            <StatusTag
              text={deviation.priority?.name ?? t("main.notSpecified")}
              color={deviation.priority === DeviationPriority.HIGH ? "red" : deviation.priority === DeviationPriority.MEDIUM ? "orange" : "green"}
            />
          ),
          rawString: deviation.priority?.name ?? t("main.notSpecified"),
        };
      }
      if (!fields || fields.includes("tag")) {
        dataTableData.cells["tag"] = {
          element: <TableTagCell tagIDs={deviation.tags} />,
          rawData: deviation.tags,
        };
      }
      if (!fields || fields.includes("tags")) {
        dataTableData.cells["tags"] = {
          element: <TableTagCell tagIDs={deviation.tags} />,
          rawData: deviation.tags,
        };
      }
      if (!fields || fields.includes("reportDate")) {
        dataTableData.cells["reportDate"] = deviation.reportDate;
      }
      if (!fields || fields.includes("lastUpdate")) {
        dataTableData.cells["lastUpdate"] = deviation.lastUpdate;
      }
      if (!fields || fields.includes("floors")) {
        dataTableData.cells["floors"] = deviation.floors.join(", ");
      }
      if (!fields || fields.includes("placeDescription")) {
        dataTableData.cells["placeDescription"] = deviation.placeDescription;
      }
      if (!fields || fields.includes("startDate")) {
        dataTableData.cells["startDate"] = deviation.startDate;
      }
      if (!fields || fields.includes("deadline")) {
        dataTableData.cells["deadline"] = deviation.endDate;
      }
      if (!fields || fields.includes("contractorGroups")) {
        dataTableData.cells["contractorGroups"] = deviation.contractorGroups.map((data) => data.name).join(", ");
      }
      if (!fields || fields.includes("responsibles")) {
        dataTableData.cells["responsibles"] = deviation.responsibles.map((data) => data.name).join(", ");
      }
      if (!fields || fields.includes("executors")) {
        dataTableData.cells["executors"] = deviation.executors.map((data) => data.name).join(", ");
      }
      if (!fields || fields.includes("archived")) {
        dataTableData.cells["archived"] = deviation.archived ? t("main.yes") : t("main.no");
      }
      if (!fields || fields.includes("files")) {
        dataTableData.cells["files"] = {
          element: <Label type="paragraph" text={deviation.files.length + " " + (deviation.files.length === 1 ? t("main.file") : t("main.files"))} />,
          rawString: deviation.files.length + " " + (deviation.files.length === 1 ? t("main.file") : t("main.files")),
          rawData: deviation.files,
        };
      }
      if (!fields || fields.includes("images")) {
        dataTableData.cells["images"] = {
          element: (
            <Label type="paragraph" text={deviation.images.length + " " + (deviation.images.length === 1 ? t("main.image") : t("main.images"))} />
          ),
          rawString: deviation.images.length + " " + (deviation.images.length === 1 ? t("main.image") : t("main.images")),
          rawData: deviation.images,
        };
      }
      if (!fields || fields.includes("totalHours")) {
        dataTableData.cells["totalHours"] = {
          cellValue: () => getTotalTaskHours(deviation),
          loader: () => loadTotalTaskHours(deviation),
        } as DataTableV2Element;
      }
      if (!fields || fields.includes("totalCostsIncludingVAT")) {
        dataTableData.cells["totalCostsIncludingVAT"] = {
          cellValue: () => getTotalTaskCosts(deviation, true),
          loader: () => loadTotalTaskCosts(deviation),
        } as DataTableV2Element;
      }
      if (!fields || fields.includes("totalCostsExcludingVAT")) {
        dataTableData.cells["totalCostsExcludingVAT"] = {
          cellValue: () => getTotalTaskCosts(deviation, false),
          loader: () => loadTotalTaskCosts(deviation),
        } as DataTableV2Element;
      }

      return dataTableData;
    });
  }

  getAllDataTableHeaders(): DataTableHeader[] {
    return [
      {
        key: "deviationNumber",
        title: "#",
        width: "30px",
      },
      {
        key: "type",
        title: t("main.type"),
      },
      {
        key: "property",
        title: t("main.property"),
      },
      { key: "units", title: t("main.units") },
      {
        key: "status",
        title: t("main.status"),
      },
      { key: "userID", title: t("main.reportedBy") },
      {
        key: "title",
        title: t("main.title"),
      },
      { key: "description", title: t("main.description") },
      {
        key: "priority",
        title: t("main.priority"),
      },
      {
        key: "tag",
        title: t("main.tag"),
      },
      {
        key: "reportDate",
        title: t("main.reportDate"),
      },
      { key: "lastUpdate", title: t("main.lastUpdated") },
      {
        key: "floors",
        title: t("main.floors"),
      },
      {
        key: "placeDescription",
        title: t("main.placeDescription"),
      },
      {
        key: "startDate",
        title: t("main.startDate"),
      },
      {
        key: "deadline",
        title: t("main.deadline"),
      },
      { key: "contractorGroups", title: t("main.contractorGroups") },
      { key: "responsibles", title: t("main.responsibles") },
      { key: "executors", title: t("main.executors") },
      { key: "archived", title: t("main.isArchived") },
      { key: "files", title: t("main.files") },
      { key: "images", title: t("main.images") },
      { key: "totalHours", title: t("tasks.totalHours.columnTitle") },
      { key: "totalCostsIncludingVAT", title: t("tasks.totalCostsIncludingVAT.columnTitle") },
      { key: "totalCostsExcludingVAT", title: t("tasks.totalCostsExcludingVAT.columnTitle") },
    ];
  }

  getLinkTo(key: string): string | undefined {
    if (key.split("-")[0].length > 1) {
      return `/tasks/${key.split("-")[1]}`;
    }
    return `/deviations?dId=${key}`;
  }

  override getDefaultFilterFields = (): DataObjectField<any, any>[] => {
    return [
      this.title,
      this.property,
      this.status,
      this.reportDate,
      this.endDate,
      this.startDate,
      this.priority,
      this.responsibles,
      this.executors,
      this.contractorGroups,
    ];
  };

  areAllLoaded(): boolean {
    return DeviationsLoader.getInstance().areAllLoaded();
  }

  override getActionsForData = (deviation: DeviationData): DataObjectAction<DeviationData>[] => {
    const actions: DataObjectAction<DeviationData>[] = [];
    const property = deviation.parent instanceof PropertyData ? deviation.parent : undefined;
    if (!property) return actions;

    if (ActionValidator.isAllowedToPerformAction("deviation_assign_responsibles_executors", [property])) {
      actions.push({
        id: "assign_responsibles",
        title: t("main.changeResponsible"),
        iconName: "person_add",
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            DeviationDataActions.editResponsible(
              deviation.responsibles.map((r) => r.id),
              deviation.contractorGroups.map((c) => c.id),
              property.id,
              deviation.units.map((u) => u.id),
              (data) => resolve({ dataObjects, data })
            );
          });
        },
        performAction: (value) => {
          return ToastUtilities.presentSavingToast(deviation.setResponsibles(value));
        },
      });
      actions.push({
        id: "assign_executors",
        title: t("main.changeExecutors"),
        iconName: "person_add",
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            DeviationDataActions.editExecutors(
              deviation.responsibles.map((r) => r.id),
              deviation.contractorGroups.map((c) => c.id),
              property.id,
              deviation.units.map((u) => u.id),
              (data) => resolve({ dataObjects, data })
            );
          });
        },
        performAction: (value) => {
          return ToastUtilities.presentSavingToast(deviation.setExecutors(value));
        },
      });
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_assign_contractors", [property])) {
      actions.push({
        id: "assign_contractors",
        title: t("main.assignContractors"),
        iconName: "build",
        requiredConsent: async (dataObjects) => {
          // Find and load all contractors
          const contractors = property.contractors;
          const contractorsToLoad = contractors.filter((contractor) => !contractor.loaded);
          if (contractorsToLoad.length > 0) await Promise.allSettled(contractorsToLoad.map((contractor) => contractor.load()));
          const options = contractors.map((contractor) => ({ key: contractor.id, title: contractor.name }));

          return new Promise((resolve) =>
            PopupTemplates.presentMultiSelectInput(t("main.selectContractor"), undefined, options, (contractorIDs) =>
              resolve({ dataObjects, data: contractorIDs })
            )
          );
        },
        performAction: (value) => {
          return ToastUtilities.presentSavingToast(
            deviation.sendDeviationUpdate(
              {
                contractorGroups: value,
              },
              undefined
            )
          );
        },
      });
    }

    const fields = DeviationDataActions.getPossibleFieldsToEdit(deviation);
    for (const fieldList of fields) {
      const field = fieldList[0];
      const updateField = fieldList[1];
      actions.push({
        id: `edit_${field.key}`,
        iconName: field.iconName ?? "edit",
        title: `${t("main.edit")} ${field.title.toLowerCase()}`,
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) =>
            DeviationDataActions.presentDeviationSetting(deviation, field, updateField, (data) => resolve({ dataObjects, data }))
          );
        },
        performAction: (value) => {
          return ToastUtilities.presentSavingToast(
            deviation.sendDeviationUpdate(
              {
                [updateField.key]:
                  value === undefined ? null : Array.isArray(value) ? value.map((val) => updateField.encode(val)) : updateField.encode(value),
              },
              undefined
            )
          );
        },
      });
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_change_category", [property])) {
      actions.push({
        id: "edit_tags",
        iconName: "tag",
        title: t("main.editTags"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <TagsSelectPopup
                id={id}
                title={t("main.editTags")}
                actionTitle={t("main.save")}
                propertyId={property.id}
                value={dataObjects[0].tags as string[]}
                onChange={(tags) => {
                  resolve({ dataObjects, data: { tags } });
                }}
              />
            ));
          });
        },
        performAction: ({ tags }) => {
          return ToastUtilities.presentSavingToast(
            deviation.sendDeviationUpdate(
              {
                tags: tags.map((tag: Tag) => tag.id),
              },
              undefined
            )
          );
        },
      });
    }

    actions.push({
      id: "edit_type",
      title: t("main.editType"),
      iconName: "build",
      requiredConsent: (dataObjects) => {
        return new Promise((resolve) => {
          const oldType = dataObjects[0].taskTypeID;
          const includesRoutineDeviations = dataObjects.some((data) => data.taskTypeID === "routine");

          Popup.present((id) => (
            <DeviationTypeSelectorPopup
              id={id}
              description={`${t("main.tooltip_delayForUpdates")}${
                includesRoutineDeviations ? ` ${t("main.theseChangesWillNotApplyToRoutines")}` : ""
              }`}
              defaultValue={oldType}
              context={{ propertyID: property.id, menuPortalTarget: "popup" }}
              onFinish={({ taskTypeID }) => {
                resolve({ dataObjects, data: { taskTypeID } });
              }}
            />
          ));
        });
      },
      performAction: ({ taskTypeID }: { taskTypeID: string }) => {
        return ToastUtilities.presentSavingToast(
          deviation.sendDeviationUpdate(
            {
              newTaskTypeID: taskTypeID,
            },
            undefined
          )
        );
      },
    });

    actions.push({
      id: "download_pdf",
      iconName: "download",
      title: t("main.downloadAsPdf"),
      performAction: () => {
        return new Promise((resolve) => {
          Popup.present((id) => <ExportDeviationPDFsPopup id={id} deviations={[deviation]} onFinished={() => resolve(null)} />);
        });
      },
      performActionOnDataObjects: (deviations) => {
        return new Promise((resolve) => {
          Popup.present((id) => <ExportDeviationPDFsPopup id={id} deviations={deviations} onFinished={() => resolve(null)} />);
        });
      },
    });

    if (ActionValidator.isAllowedToPerformAction("deviation_archive", [property])) {
      const id = deviation.archived ? "unarchive" : "archive";
      actions.push({
        id,
        iconName: "archive",
        title: `${t(deviation.archived ? "main.unarchive" : "main.archive")} ${t("main.task").toLowerCase()}`,
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyAction(
              deviation.archived ? t("main.unarchiveTask") : `${t("main.archiveTask")}?`,
              deviation.archived ? t("main.taskDearchivationCaution") : t("main.taskArchivationCaution"),
              () => {
                resolve({ dataObjects });
              }
            );
          });
        },
        performAction: () => {
          return ToastUtilities.presentSavingToast(deviation.archive());
        },
      });
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_delete", [property])) {
      actions.push({
        id: "delete",
        iconName: "delete",
        iconColor: "var(--main-red-700)",
        title: `${t("main.delete")} ${t("main.task").toLowerCase()}`,
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyDeleteDatas(
              this,
              dataObjects.map((data) => data.title),
              () => {
                resolve({ dataObjects });
              },
              t("main.deviationDeletionCaution")
            );
          });
        },
        performAction: () => {
          return ToastUtilities.presentDeletingToast(deviation.delete());
        },
      });
    }

    return actions;
  };

  getDefaultTableSortDirection(): "ascending" | "descending" {
    return "descending";
  }
  getDefaultTableSortKey(): string | undefined {
    return "lastUpdate";
  }
}

const DeviationTypeCell = ({ taskTypeID }: { taskTypeID: string }) => {
  const { taskType } = useTaskTypeById({ taskTypeId: taskTypeID });

  return <>{taskType ? translate(taskType.title) : ""}</>;
};
